import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { CellContext, createColumnHelper } from '@tanstack/react-table';

import useNow from 'hooks/useNow';
import { duration, elapsed, simpleDate, simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { Tab } from 'stores/inboundReferralsStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';

import {
  INBOUND_REFERRALS_ROUTES,
  isPortalWorkflow,
  PORTAL_DISCOVERY_API_NAME,
  selectedGroupName,
  VALUE_NAMES,
} from './constants';
import {
  categorySegments,
  isOpenSweep,
  LIST_STATUS_DISPLAY,
  overallListStatus,
  progressSummary,
} from './referralProgress';
import { sentToSummary } from './ReferralSentTo';
import SNFSlideout from './SNFSlideout';
import { Tone, TONE_STYLES } from './StatusBadge';

const columnHelper = createColumnHelper<Workflow>();

// Its own component so the clock ticks independently of data fetches (OLIO-2602):
// server timestamps stay the source of truth; the tick only advances the display.
function ElapsedCell({ created }: { created: Date | null }) {
  const now = useNow(30_000);
  if (!created) return <>—</>;
  return (
    <TimeCell>
      <Duration>{elapsed(created, now)}</Duration>
      <Timestamp>{simpleDateAtTime(created)}</Timestamp>
    </TimeCell>
  );
}

// History's Time Spent (OLIO-3295): creation to the conclusion — the outcome, or the archive/cancel
// — a fixed span, so no clock. A row with no conclusion has nothing to measure and says so, rather
// than falling back to a ticking age under a header that promises a finished figure.
function TimeSpentCell({ created, concluded }: { created: Date | null; concluded: Date | null }) {
  if (!created || !concluded) return <>—</>;
  return (
    <TimeCell>
      <Duration>{duration(created, concluded)}</Duration>
      <Timestamp>{simpleDateAtTime(created)}</Timestamp>
    </TimeCell>
  );
}

// A discovery sweep is anchored to a portal, not a referral, so it has no patient and no
// insurance. It shares this table with retrieval runs (OLIO-2758), so the patient-shaped columns
// must not present a sweep as a referral with missing data — `patientName` in particular falls
// back to the literal 'New referral'.
function isDiscovery(workflow: Workflow): boolean {
  return workflow.definition.apiName === PORTAL_DISCOVERY_API_NAME;
}

// The portal a run concerns. Every portal run carries source_system — it's the only value a
// sweep has, and retrieval runs carry it alongside their referral data — so this earns its own
// column rather than being crammed into the patient cell.
//
// The name comes from the caller (usePortalName) rather than a lookup here: portal names are
// the server's, and a column factory is not a component, so it can't hold a query of its own.
function portalLabel(workflow: Workflow, portalName: PortalName): string {
  return portalName(workflow.valueFor('source_system')?.value);
}

// What the subject cell says when there's no patient name. A sweep has no patient at all; a
// portal retrieval run has none *yet* (extraction fills it in later) and the bare 'New referral'
// fallback reads as a referral someone forgot to name.
//
// Deliberately NOT suffixed with the portal ("Portal referral — Faux-Port"): the Portal column
// right next door already says which portal, so the suffix was the same word twice per row. The
// detail page still suffixes, because it has no Portal column to lean on.
//
// Only for PORTAL runs: these columns are shared with the inbound-referrals inbox (a
// referral-intake run genuinely is a new referral, so it keeps Workflow#patientName's wording).
function subjectLabel(workflow: Workflow): string {
  if (workflow.hasPatientName) return workflow.patientName;
  // An open sweep pins to the top of the list (OLIO-2814); the suffix says why it's first so
  // the position doesn't read as an ordering quirk. Copy only — UX may replace it with a
  // dedicated affordance later. Drops with the pin once the sweep is handed off.
  if (isDiscovery(workflow)) return isOpenSweep(workflow) ? 'Portal sweep — needs review' : 'Portal sweep';
  if (!isPortalWorkflow(workflow.definition.apiName)) return workflow.patientName;
  return 'Portal referral';
}

// The portal-name resolver the caller passes down — hooks/usePortalConnections' usePortalName.
type PortalName = (sourceSystem?: string | null) => string;

// Hoisted out of the column factory below so it keeps one stable identity across every call to
// inboundReferralsTableColumns — react-table's flexRender passes a `cell` def straight to
// React.createElement as the element's type, so a fresh closure (as this would be if it were
// defined inline, per every other cell here) makes React treat each recomputed columns array as
// a brand-new component type and remount the cell on every recompute. SNFCell has to survive
// that: it owns the slideout's open/closed state, and a remount silently snaps it shut.
function snfCell(info: CellContext<Workflow, unknown>) {
  return <SNFCell workflow={info.row.original} />;
}

// History's SNF cell also names the winner under a multi-SNF link (OLIO-3295); a second hoisted
// identity for the same reason as the first.
function snfHistoryCell(info: CellContext<Workflow, unknown>) {
  return <SNFCell workflow={info.row.original} showSelected />;
}

const sharedColumns = (portalName: PortalName) => [
  columnHelper.accessor((row) => subjectLabel(row), {
    id: 'patientName',
    header: 'Patient Name',
    cell: (info) => {
      const to = `${INBOUND_REFERRALS_ROUTES.BASE}/${info.row.original.id}`;
      return info.row.original.hasPatientName ? (
        <PatientLink to={to}>{info.getValue()}</PatientLink>
      ) : (
        <SweepLabel to={to} data-testid='workflow-type'>
          {info.getValue()}
        </SweepLabel>
      );
    },
    size: 200,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => portalLabel(row, portalName), {
    id: 'portal',
    header: 'Portal',
    // Useful for both types: which portal this work concerns.
    cell: (info) => info.getValue() || '—',
    size: 110,
    enableSorting: false,
  }),
  // The primary coverage's own payer, with an older referral's captured payer string as the
  // fallback (OLIO-2890). The model owns both halves so the coverage scoping lives in one place.
  columnHelper.accessor((row) => row.primaryPayerName, {
    id: 'payer',
    header: 'Payer',
    // Not applicable to a sweep (no patient, no insurance) — blank rather than a dash, so the
    // row doesn't read as a referral whose payer is merely unknown.
    cell: (info) => (isDiscovery(info.row.original) ? '' : info.getValue() || '—'),
    size: 120,
    enableSorting: false,
  }),
  // The name it RESOLVED to and nothing else, so the column agrees with the filter beside it
  // (OLIO-3161): several wordings share one reporting name, and this column is what the filter
  // matches on.
  //
  // Deliberately empty until it resolves, rather than falling back to the wording the referral
  // captured. The fallback made a referral that has resolved to nothing look like it had a source,
  // which is the opposite of what the row needs to say, and it read as a contradiction next to the
  // filter's own "None" option: those rows came back under None with text in this column. The
  // wording is still on the referral page, where it belongs.
  columnHelper.accessor((row) => row.resolvedAttrValue(VALUE_NAMES.REFERRAL_SOURCE) ?? '', {
    id: 'referralSource',
    header: 'Referral Source',
    cell: (info) => (isDiscovery(info.row.original) ? '' : info.getValue() || '—'),
    size: 150,
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'snf',
    header: 'SNF',
    size: 130,
    cell: snfCell,
  }),
  columnHelper.display({
    id: 'progress',
    header: 'Progress',
    size: 190,
    cell: (info) => {
      const workflow = info.row.original;
      const { index, total, categoryName } = progressSummary(workflow);
      return (
        <ProgressCell>
          <Bar data-testid='progress-bar'>
            {categorySegments(workflow).map((segment) => (
              <Segment key={segment.key} $color={TONE_STYLES[LIST_STATUS_DISPLAY[segment.status].tone].accent} />
            ))}
          </Bar>
          <ProgressLabel>{total > 0 ? `${index} of ${total} – ${categoryName}` : '—'}</ProgressLabel>
        </ProgressCell>
      );
    },
  }),
  columnHelper.display({
    id: 'currentStatus',
    header: 'Current Status',
    size: 140,
    cell: (info) => {
      const status = overallListStatus(info.row.original);
      const display = LIST_STATUS_DISPLAY[status];
      return (
        <Pill $tone={display.tone} data-testid='list-status'>
          {status === 'processing' && <AISparkleIcon width={12} height={12} color={TONE_STYLES[display.tone].color} />}
          {display.label}
        </Pill>
      );
    },
  }),
  columnHelper.accessor((row) => row.createdAt, {
    id: 'timeElapsed',
    header: 'Time Elapsed',
    size: 150,
    enableSorting: false,
    cell: (info) => <ElapsedCell created={info.getValue() as Date | null} />,
  }),
];

// The History tab's own columns (OLIO-3295). Progress is gone — every row here is finished; Time
// Elapsed becomes Time Spent, a fixed span to the conclusion; and Date Completed, the conclusion
// itself, closes the row and is the tab's default sort (the only sortable column in the table —
// the api sorts on concluded_at, undated rows last).
const historyColumns = () => [
  columnHelper.accessor((row) => row.createdAt, {
    id: 'timeSpent',
    header: 'Time Spent',
    size: 150,
    enableSorting: false,
    cell: (info) => (
      <TimeSpentCell created={info.getValue() as Date | null} concluded={info.row.original.concludedAt} />
    ),
  }),
  columnHelper.accessor((row) => row.concludedAt, {
    // The id doubles as the sort key the api receives (serializeSort), so it names the column.
    id: 'concludedAt',
    header: 'Date Completed',
    size: 140,
    enableSorting: true,
    cell: (info) => {
      const concluded = info.getValue() as Date | null;
      return concluded ? simpleDate(concluded) : '—';
    },
  }),
];

const inboundReferralsTableColumns = (portalName: PortalName, { tab = 'active' }: { tab?: Tab } = {}) => {
  const shared = sharedColumns(portalName);
  if (tab !== 'history') return shared;

  return shared
    .filter((column) => column.id !== 'progress' && column.id !== 'timeElapsed')
    .map((column) => (column.id === 'snf' ? { ...column, cell: snfHistoryCell } : column))
    .concat(historyColumns());
};

export default inboundReferralsTableColumns;

const PatientLink = styled(Link)`
  font-weight: 500;
  color: ${colors.blue100};
  text-decoration: none;
`;

// A sweep's identity cell. Single-line so a discovery row is the same height as a referral row,
// and muted/italic rather than link-blue because there's no patient behind it.
const SweepLabel = styled(Link)`
  font-weight: 500;
  font-style: italic;
  color: ${colors.black50};
  text-decoration: none;
`;

const ProgressCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Bar = styled.div`
  display: flex;
  gap: 2px;
`;

const Segment = styled.div<{ $color: string }>`
  height: 9px;
  width: 27px;
  flex-shrink: 0;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

const ProgressLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const Pill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;

const TimeCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const Duration = styled.span`
  color: ${colors.black100};
`;

const Timestamp = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

// `showSelected` (History, OLIO-3295): under a multi-SNF link, name the building that was selected,
// so the row answers "which one?" without the slideout. A single-SNF row IS its own answer.
function SNFCell({ workflow, showSelected = false }: { workflow: Workflow; showSelected?: boolean }) {
  const [open, setOpen] = useState(false);
  const groups = workflow.groups;

  if (isDiscovery(workflow)) return null;
  if (groups.length === 0) return <>{'—'}</>;
  if (groups.length === 1) return <>{groups[0].name}</>;

  const selected = showSelected ? selectedGroupName(workflow) : null;

  return (
    <>
      {/* A column, not siblings: the table cell lays its children out inline, and the sublabel
          belongs under the link, not beside it. */}
      <SNFStack>
        <SNFLink type='button' onClick={() => setOpen(true)}>
          {sentToSummary(groups)}
        </SNFLink>
        {selected && <SelectedLabel data-testid='snf-selected'>Selected: {selected}</SelectedLabel>}
      </SNFStack>
      {/* Always mounted so Foldout can animate on the false→true open transition. */}
      <SNFSlideout open={open} workflow={workflow} onClose={() => setOpen(false)} />
    </>
  );
}

const SNFStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const SelectedLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const SNFLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  text-decoration: underline;
  cursor: pointer;
`;
